import { NAMESPACE } from "./catalog";
import { abilityCommands } from "./commands";
import { entityData, giveCommand, itemComponents } from "./components";
import { toSnbt } from "./snbt";
import type { Creation, HitPower } from "./types";

export type FileMap = Record<string, string>;

const DATA_MIN_FORMAT = 94;
const MAX_FORMAT = 999;
const TICKS_PER_SECOND = 20;
const NS = NAMESPACE;
const FN = `data/${NS}/function`;
const MENU_TRIGGER = `${NS}_menu`;
const GET_TRIGGER = `${NS}_get`;
const SUMMON_OFFSET = 1000;
export const PACK_DESCRIPTION = "My Mods - made with mods.maniginam.dev";

export function withUniqueIds<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.map((item) => {
    let id = item.id;
    for (let n = 2; seen.has(id); n++) id = `${item.id}_${n}`;
    seen.add(id);
    return { ...item, id };
  });
}

export function buildDatapack(input: Creation[]): FileMap {
  const creations = withUniqueIds(input);
  const withAbility = creations.filter((c) => c.ability);
  const withRecipe = creations.filter((c) => c.recipe);
  const files: FileMap = {
    "pack.mcmeta": json({ pack: { description: PACK_DESCRIPTION, min_format: DATA_MIN_FORMAT, max_format: MAX_FORMAT } }),
    "data/minecraft/tags/function/load.json": json({ values: [`${NS}:load`] }),
    "data/minecraft/tags/function/tick.json": json({ values: [`${NS}:tick`] }),
    [`${FN}/load.mcfunction`]: lines([
      ...withAbility.map((c) => `scoreboard objectives add ${cooldownObjective(c)} dummy`),
      `scoreboard objectives add ${MENU_TRIGGER} trigger`,
      `scoreboard objectives add ${GET_TRIGGER} trigger`,
      ...(withRecipe.length ? [`advancement revoke @a only ${NS}:unlock_recipes`] : []),
      `advancement revoke @a only ${NS}:welcome`,
    ]),
    [`${FN}/welcome.mcfunction`]: lines([
      `tellraw @s [{text:"My Mods is here! ",color:"gold",bold:true},{text:"Type /trigger ${MENU_TRIGGER} or click here",color:"green",underlined:true,` +
      `click_event:{action:"run_command",command:"/trigger ${MENU_TRIGGER}"}}]`,
    ]),
    [`data/${NS}/advancement/welcome.json`]: json({
      criteria: { join: { trigger: "minecraft:tick" } },
      rewards: { function: `${NS}:welcome` },
    }),
    [`${FN}/tick.mcfunction`]: lines([
      ...withAbility.map((c) => `scoreboard players remove @a[scores={${cooldownObjective(c)}=1..}] ${cooldownObjective(c)} 1`),
      `execute as @a[scores={${MENU_TRIGGER}=1..}] run function ${NS}:menu`,
      `scoreboard players set @a ${MENU_TRIGGER} 0`,
      ...creations.flatMap((c, i) => [
        `execute as @a[scores={${GET_TRIGGER}=${i + 1}}] at @s run function ${NS}:give/${c.id}`,
        ...(c.mob ? [`execute as @a[scores={${GET_TRIGGER}=${SUMMON_OFFSET + i + 1}}] at @s run function ${NS}:summon/${c.id}`] : []),
      ]),
      `scoreboard players set @a ${GET_TRIGGER} 0`,
      `scoreboard players enable @a ${MENU_TRIGGER}`,
      `scoreboard players enable @a ${GET_TRIGGER}`,
    ]),
    [`${FN}/menu.mcfunction`]: lines(menuLines(creations)),
  };
  if (withRecipe.length) {
    files[`data/${NS}/advancement/unlock_recipes.json`] = json({
      criteria: { join: { trigger: "minecraft:tick" } },
      rewards: { recipes: withRecipe.map((c) => `${NS}:${c.id}`) },
    });
  }
  for (const c of creations) Object.assign(files, creationFiles(c));
  return files;
}

function creationFiles(c: Creation): FileMap {
  const files: FileMap = { [`${FN}/give/${c.id}.mcfunction`]: lines([giveCommand(c)]) };
  if (c.mob) {
    const { id: _entityId, ...data } = entityData(c.name, c.mob);
    files[`${FN}/summon/${c.id}.mcfunction`] = lines([`summon minecraft:${c.mob.entity} ~ ~ ~ ${toSnbt(data)}`]);
  }
  if (c.hitPowers.length) files[`data/${NS}/enchantment/${c.id}.json`] = json(enchantment(c));
  if (c.ability) {
    files[`data/${NS}/advancement/use/${c.id}.json`] = json(useAdvancement(c));
    files[`${FN}/ability/${c.id}.mcfunction`] = lines([
      `advancement revoke @s only ${NS}:use/${c.id}`,
      `execute if score @s ${cooldownObjective(c)} matches 1.. run return fail`,
      `scoreboard players set @s ${cooldownObjective(c)} ${Math.round(c.cooldownSeconds * TICKS_PER_SECOND)}`,
      ...abilityCommands(c.ability),
    ]);
  }
  if (c.recipe) files[`data/${NS}/recipe/${c.id}.json`] = json(recipe(c));
  return files;
}

function menuLines(creations: Creation[]): string[] {
  return [
    `tellraw @s {text:"=== My Mods (click to get) ===",color:"gold",bold:true}`,
    ...creations.map((c, i) => {
      const parts = [
        button("[Get] ", "green", `/trigger ${GET_TRIGGER} set ${i + 1}`),
        ...(c.mob ? [button("[Summon] ", "red", `/trigger ${GET_TRIGGER} set ${SUMMON_OFFSET + i + 1}`)] : []),
        { text: c.name, color: "aqua" },
      ];
      return `tellraw @s ${toSnbt(parts)}`;
    }),
  ];
}

function button(text: string, color: string, command: string) {
  return {
    text, color,
    click_event: { action: "run_command", command },
    hover_event: { action: "show_text", value: "Click me!" },
  };
}

function enchantment(c: Creation) {
  return {
    description: `${c.name} Powers`,
    supported_items: `minecraft:${c.baseItem}`,
    weight: 1,
    max_level: 1,
    min_cost: { base: 1, per_level_above_first: 0 },
    max_cost: { base: 1, per_level_above_first: 0 },
    anvil_cost: 1,
    slots: ["mainhand"],
    effects: { "minecraft:post_attack": c.hitPowers.map(postAttack) },
  };
}

function postAttack(power: HitPower) {
  const onVictim = (effect: object) => ({ enchanted: "attacker", affected: "victim", effect });
  switch (power.type) {
    case "effect":
      return onVictim(mobEffect(power.effect, power.seconds, power.level));
    case "self_effect":
      return { enchanted: "attacker", affected: "attacker", effect: mobEffect(power.effect, power.seconds, power.level) };
    case "ignite":
      return onVictim({ type: "minecraft:ignite", duration: power.seconds });
    case "lightning":
      return onVictim({ type: "minecraft:summon_entity", entity: "minecraft:lightning_bolt" });
    case "extra_damage":
      return onVictim({
        type: "minecraft:damage_entity", damage_type: "minecraft:magic", min_damage: power.amount, max_damage: power.amount,
      });
  }
}

function mobEffect(effect: string, seconds: number, level: number) {
  return {
    type: "minecraft:apply_mob_effect",
    to_apply: `minecraft:${effect}`,
    min_duration: seconds,
    max_duration: seconds,
    min_amplifier: level - 1,
    max_amplifier: level - 1,
  };
}

function useAdvancement(c: Creation) {
  return {
    criteria: {
      use: {
        trigger: "minecraft:using_item",
        conditions: {
          item: { items: `minecraft:${c.baseItem}`, predicates: { "minecraft:custom_data": { [NS]: c.id } } },
        },
      },
    },
    rewards: { function: `${NS}:ability/${c.id}` },
  };
}

function recipe(c: Creation) {
  const { pattern, key } = c.recipe!;
  return {
    type: "minecraft:crafting_shaped",
    category: ["weapon", "tool", "armor"].includes(c.kind) ? "equipment" : "misc",
    pattern,
    key: Object.fromEntries(Object.entries(key).map(([symbol, item]) => [symbol, `minecraft:${item}`])),
    result: { id: `minecraft:${c.baseItem}`, count: 1, components: itemComponents(c) },
  };
}

function cooldownObjective(c: Creation): string {
  return `${NS}_cd_${c.id}`;
}

function json(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function lines(commands: string[]): string {
  return commands.join("\n") + "\n";
}
